import {
    DeserializeCallbackFunc,
    IDeserializeContext,
    IModelSchema,
    Primitive
} from '@mintdata/mint-kit/impl/util/serializr/src/api/types';
import {GUARDED_NOOP, invariant, isAssignableTo, once} from '../utils/utils';

interface IPendingRef
{
    modelSchema:IModelSchema;
    uuid:string;
    callback:DeserializeCallbackFunc;
}

interface IResolvedRef
{
    modelSchema:IModelSchema;
    value:any;
}

export class Context implements IDeserializeContext
{
    public readonly isRoot:boolean;
    public pendingCallbacks:number = 0;
    public pendingRefsCount:number = 0;
    public target:any = null;
    public hasError:boolean = false;
    public readonly rootContext:IDeserializeContext = null;
    public readonly args:Array<any> = null;
    public readonly pendingRefs:{[uuid:string]:Array<IPendingRef>} = null;
    public readonly resolvedRefs:{[uuid:string]:Array<IResolvedRef>} = null;

    public constructor(public readonly parentContext:IDeserializeContext,
                       public readonly modelSchema:IModelSchema,
                       public readonly json:any,
                       public readonly onReadyCb:DeserializeCallbackFunc,
                       customArgs:Array<any>)
    {
        this.isRoot = !parentContext;
        this.onReadyCb = onReadyCb || GUARDED_NOOP;

        if( this.isRoot )
        {
            this.rootContext = this;
            this.args = customArgs;
            this.pendingRefs = {};
            this.resolvedRefs = {};
        }
        else
        {
            this.rootContext = parentContext.rootContext;
            this.args = parentContext.args;
        }
    }

    createCallback(fn:Function):Function
    {
        this.pendingCallbacks++;

        // once: defend against user-land calling 'done' twice
        return once((err, value) =>
        {
            if( err )
            {
                if( !this.hasError )
                {
                    this.hasError = true;
                    this.onReadyCb(err);
                }
            }
            else if( !this.hasError )
            {
                fn(value);

                if( --this.pendingCallbacks === this.pendingRefsCount )
                {
                    if( this.pendingRefsCount > 0 )
                        // all pending callbacks are pending reference resolvers. not good.
                        this.onReadyCb(
                            new Error(
                                'Unresolvable references in json: \"' +
                                Object.keys(this.pendingRefs).filter((uuid) =>
                                {
                                    return this.pendingRefs[uuid].length > 0;
                                }).join('\", \"') +
                                '\"'
                            )
                        );
                    else
                        this.onReadyCb(null, this.target);
                }
            }
        });
    }

    // given an object with uuid, modelSchema, callback, awaits until the given uuid is available
    // resolve immediately if possible
    await(modelSchema:IModelSchema, id:Primitive, callback:DeserializeCallbackFunc):void
    {
        invariant(this.isRoot);

        const uuid:string = String(id);

        if( uuid in this.resolvedRefs )
        {
            const match = this.resolvedRefs[uuid].find(function(resolved)
            {
                return isAssignableTo(resolved.modelSchema, modelSchema);
            });

            if( match )
                return callback(null, match.value);
        }

        this.pendingRefsCount++;

        if( !this.pendingRefs[uuid] )
            this.pendingRefs[uuid] = [];

        this.pendingRefs[uuid].push(
            {
                modelSchema,
                uuid,
                callback
            }
        );
    }

    // given a model schema, id and value, resolve all references that where looking for this object
    resolve(modelSchema:IModelSchema, id:Primitive, value:any):void
    {
        invariant(this.isRoot);

        const uuid:string = String(id);

        if( !this.resolvedRefs[uuid] )
            this.resolvedRefs[uuid] = [];

        this.resolvedRefs[uuid].push(
            {
                modelSchema,
                value
            }
        );

        if( uuid in this.pendingRefs )
        {
            for( let i:number = this.pendingRefs[uuid].length - 1; i >= 0; i-- )
            {
                const opts:IPendingRef = this.pendingRefs[uuid][i];

                if( isAssignableTo(modelSchema, opts.modelSchema) )
                {
                    this.pendingRefs[uuid].splice(i, 1);
                    this.pendingRefsCount--;

                    opts.callback(null, value);
                }
            }
        }
    }
}
